import { computed, reactive, watch, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQueryRaw, type RouteLocationRaw } from 'vue-router'

/** Lo que un filtro puede guardar. */
export type ValorFiltro = string | number | boolean | null | undefined

/** Cómo se declara un filtro para que viva en la URL. */
export interface CampoFiltro {
  ref: Ref<ValorFiltro>
  /**
   * Texto libre. Es lo único que se acepta tal cual, sin validar.
   *
   * Un término de búsqueda es texto libre de verdad: no hay lista de términos válidos. Y
   * aun así no es innocuo —`?busqueda=<script>` acaba en la consulta—, así que el valor
   * se pasa por `encodeURIComponent` al escribirlo.
   */
  texto?: boolean
  /** Los valores que se aceptan desde la URL. Sin la lista, es texto libre. */
  permitidos?: (string | number | boolean | null)[]
  /**
   * Cualquier entero positivo vale.
   *
   * Para los filtros que apuntan a algo que viene de la API —el asesor—, cuya lista no se
   * conoce al montar. `permitidos` no puede usarse ahí: la lista se pide al servidor y
   * llega después, así que al leer la URL todavía está vacía y `permitidos` rechazaría
   * todo.
   *
   * Se acepta cualquier entero positivo y no un id de la lista actual a propósito: si el
   * asesor desaparece, el enlace tiene que seguir llevándolo a **su** lista —que sale
   * vacía— y no a la lista entera. Un filtro que se cae al pegar el enlace es peor.
   */
  entero?: boolean
  /** Se guarda como número. Sin esto, la página vuelve como `"3"`. */
  numero?: boolean
  /**
   * Una fecha en `YYYY-MM-DD`, y solo eso.
   *
   * Las fechas no admiten una lista de valores permitidos, así que hace falta una
   * forma de validarlas aparte: el conjunto de fechas válidas es infinito y no cabe
   * en un array. Lo que se comprueba es la **forma**, no que la fecha exista: `2026-02-31`
   * pasa el filtro y es el backend, que es quien sabe de meses, quien la rechaza con
   * su regla `date`. Aquí solo se descarta lo que ni siquiera tiene aspecto de fecha.
   *
   * Sin esto, `?desde=hasta-el-mes` acabaría en la consulta al backend tal cual.
   */
  fecha?: boolean
  /** El valor que significa «sin filtro». */
  vacio?: ValorFiltro
}

/**
 * Lo que devuelve el composable.
 *
 * Se devuelve un objeto **reactivo** y no uno con dentro los valores ya calculados. Con un
 * objeto normal, `filtros.alguno` sería el `computed` entero —un objeto, no un booleano— y
 * quien lo usara desde una prueba o desde el código se encontraría con
 * `filtros.alguno.value`. La plantilla lo desenvuelve por su cuenta y por eso el fallo
 * solo salía fuera de ella. Con `reactive` se lee igual en los dos sitios.
 */
export interface FiltrosEnUrl {
  /** La consulta con los filtros de ahora, para llevársela a otro sitio. */
  consulta: LocationQueryRaw
  /** Si hay algún filtro puesto. */
  alguno: boolean
  /** Un destino con los filtros de ahora encima. */
  conQuery: (destino: RouteLocationRaw) => RouteLocationRaw
  /** Vacía todos los filtros y vuelve a la primera página. */
  limpiar: () => void
}

/**
 * Los filtros de un listado, guardados en la URL.
 *
 * El motivo de que estén aquí y no en el componente es que un `ref` no sobrevive a que te
 * vayas a otra vista. Aplicabas un filtro, abrías una cotización, volvías, y el filtro había
 * desaparecido: el componente de antes se destruyó y el nuevo nace con los `ref` vacíos.
 * No era el botón de atrás, era que el filtro no tenía dónde quedarse.
 *
 * La URL sí: sobrevive a volver, a recargar, a compartir el enlace y al botón de atrás del
 * navegador, sin un almacén aparte que haya que mantener al día.
 *
 * ## El bucle
 *
 * Escribir la URL dispara el observador de la ruta, que vuelve a escribir los `ref`, que
 * vuelven a escribir la URL. Por eso **no** se observa la ruta para escribir los `ref`
 * desde el principio: se escribe en los dos sentidos, y antes de escribir se compara. Si
 * lo que se va a escribir es lo mismo que ya hay, no se escribe y el bucle se corta solo.
 *
 * ## Los números
 *
 * Un número en la URL es texto. Sin `numero: true`, la página vuelve como `"3"` y
 * `page: "3"` llega al backend como cadena. Con `numero: true` se lee y se devuelve como
 * número, y un valor que no es número se descarta en vez de propagarse.
 */
export function useFiltrosEnUrl(campos: Record<string, CampoFiltro>): FiltrosEnUrl {
  const route = useRoute()
  const router = useRouter()

  /**
   * El valor que significa «sin filtro» en cada campo.
   *
   * En un campo numérico es `1` y no `''`: un número no tiene un valor vacío de verdad, y
   * vaciarlo a `''` lo deja como texto y lo manda al backend como `page: ""`. La página
   * empieza en uno, y `desdeUrl` ya rechaza el cero y todo lo que no sea entero, así que
   * dejarlo en uno es lo coherente con el resto del composable.
   */
  const vacios: Record<string, ValorFiltro> = {}

  for (const [clave, campo] of Object.entries(campos)) {
    vacios[clave] = campo.vacio ?? (campo.numero === true ? 1 : '')
  }

  /** Cómo se convierte un valor de la URL en el valor del filtro. `undefined` si no vale. */
  function desdeUrl(clave: string, bruto: unknown): ValorFiltro | undefined {
    const texto = Array.isArray(bruto) ? bruto[0] : bruto

    if (texto === null || texto === undefined || texto === '') return vacios[clave]

    const campo = campos[clave]

    if (campo.numero === true) {
      const numero = Number(texto)

      // `Number('')` es 0 y `Number('abc')` es `NaN`: los dos tienen que quedarse fuera.
      // Una página «abc» en la URL no puede acabar en la consulta al backend.
      if (!Number.isInteger(numero) || numero < 1) return undefined

      return numero
    }

    if (campo.entero === true) {
      const numero = Number(texto)

      if (!Number.isInteger(numero) || numero < 1) return undefined

      return numero
    }

    if (campo.fecha === true) {
      // `new Date(texto)` acepta cosas que no son fechas, como `"hola"`, y devuelve
      // una `Invalid Date`, así que no sirve para validar. Se comprueba la forma.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(texto))) return undefined

      return String(texto)
    }

    if (campo.texto === true) return String(texto)

    if (campo.permitidos === undefined) return String(texto)

    const valor = campo.permitidos.find((permitido) => String(permitido) === String(texto))

    return valor === undefined ? undefined : valor
  }

  /** La consulta con los campos que tienen valor. Los vacíos no van: `?x=` es ruido. */
  function consultaActual(): LocationQueryRaw {
    const consulta: LocationQueryRaw = {}

    for (const [clave, campo] of Object.entries(campos)) {
      const valor = campo.ref.value

      if (valor === vacios[clave] || valor === null || valor === undefined) continue

      consulta[clave] = campo.numero === true ? String(valor) : String(valor)
    }

    return consulta
  }

  /** Escribe los filtros en la URL, pero solo si han cambiado de verdad. */
  function sincronizar(): void {
    const deseada = consultaActual()
    const actual = route.query
    const claves = Object.keys(deseada)
    const yaEstaIgual =
      claves.length === Object.keys(actual).length && claves.every((clave) => actual[clave] === deseada[clave])

    // Sin esta comparación el ciclo no termina: escribir la ruta dispara el observador.
    if (yaEstaIgual) return

    void router.replace({ query: deseada })
  }

  /** Rellena los `ref` con lo que venga en la URL, sin pisar lo que ya hay. */
  function leerUrl(): void {
    for (const [clave, campo] of Object.entries(campos)) {
      const valor = desdeUrl(clave, route.query[clave])

      if (valor === undefined) continue
      if (campo.ref.value !== valor) campo.ref.value = valor
    }
  }

  leerUrl()

  // Los `ref` mandan: son los que mueve el `v-model` de los controles.
  for (const campo of Object.values(campos)) {
    watch(campo.ref, () => sincronizar())
  }

  // Y la ruta también, para el botón de atrás del navegador y para un enlace pegado.
  watch(
    () => route.query,
    () => leerUrl(),
  )

  const consulta = computed(() => consultaActual())
  const alguno = computed(() => Object.keys(consulta.value).length > 0)

  /**
   * Un destino con los filtros de ahora encima.
   *
   * Es lo que arregla el «Volver»: el botón empuja una entrada **nueva** hacia el listado.
   * Si esa entrada no lleva la consulta, el listado vuelve a mostrarlos todos —el
   * historial no ayuda, porque la entrada anterior ya no es la que se está usando.
   */
  function conQuery(destino: RouteLocationRaw): RouteLocationRaw {
    const filtros = consultaActual()

    if (typeof destino === 'string') return { path: destino, query: filtros } as RouteLocationRaw

    if (Object.keys(filtros).length === 0) return destino

    return { ...destino, query: { ...filtros, ...((destino as { query?: LocationQueryRaw }).query ?? {}) } }
  }

  function limpiar(): void {
    for (const [clave, campo] of Object.entries(campos)) campo.ref.value = vacios[clave]

    // `replace` y no `push`: limpiar no es un sitio al que volver con el botón de atrás.
    void router.replace({ query: {} })
  }

  return reactive({ consulta, alguno, conQuery, limpiar })
}